import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
    AccessTimeOutlined,
    AttachMoneyOutlined,
    CheckCircleOutlined,
    CancelOutlined,
    EditNoteOutlined,
    ReceiptLongOutlined,
} from '@mui/icons-material';

function SummaryPill({ icon, count, label, sublabel, accentColor, accentBg, onClick, style, urgent }) {
    return (
        <button
            onClick={onClick}
            className={[
                'relative flex items-center gap-4 w-full',
                'bg-white rounded-[12px] px-5 py-4',
                'cursor-pointer text-left border',
                urgent ? 'border-[#FECACA]' : 'border-[#E2E8ED]',
                'hover:shadow-[0_4px_16px_rgba(2,45,65,0.1)] hover:-translate-y-[2px]',
                'transition-all duration-200 active:scale-[0.98]',
            ].join(' ')}
            style={{
                borderLeftWidth: '4px',
                borderLeftColor: accentColor,
                boxShadow: '0 1px 3px rgba(2,45,65,0.06), 0 4px 12px rgba(2,45,65,0.06)',
                ...style,
            }}
        >
            {urgent && count > 0 && (
                <span
                    className="absolute top-3 right-3 w-2 h-2 rounded-full bg-[#EF4444]"
                    style={{ animation: 'pulse-dot 1.8s ease-in-out infinite' }}
                />
            )}

            <div
                className="flex items-center justify-center w-10 h-10 rounded-[10px] shrink-0"
                style={{ backgroundColor: accentBg }}
            >
                {React.cloneElement(icon, { sx: { fontSize: 20, color: accentColor } })}
            </div>

            <div className="min-w-0">
                <div className="text-[28px] font-bold text-[#022D41] leading-none tabular-nums">
                    {count ?? '—'}
                </div>
                <div className="text-[13px] text-[#6B7A85] mt-1 leading-tight font-medium">
                    {label}
                </div>
                {sublabel && (
                    <div className="text-[11px] text-[#9AA3AE] mt-0.5 leading-tight">
                        {sublabel}
                    </div>
                )}
            </div>
        </button>
    );
}

function SummaryChip({ icon, count, label, accentColor, accentBg, onClick, urgent, style }) {
    return (
        <button
            onClick={onClick}
            style={style}
            className={[
                'relative flex items-center gap-2.5 shrink-0',
                'bg-white rounded-full pl-2 pr-4 py-2',
                'border', urgent ? 'border-[#FECACA]' : 'border-[#E2E8ED]',
                'cursor-pointer active:scale-[0.96] transition-transform duration-150',
                'shadow-[0_1px_3px_rgba(2,45,65,0.06)]',
            ].join(' ')}
        >
            {urgent && count > 0 && (
                <span
                    className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full bg-[#EF4444]"
                    style={{ animation: 'pulse-dot 1.8s ease-in-out infinite' }}
                />
            )}
            <span
                className="flex items-center justify-center w-7 h-7 rounded-full shrink-0"
                style={{ backgroundColor: accentBg }}
            >
                {React.cloneElement(icon, { sx: { fontSize: 15, color: accentColor } })}
            </span>
            <span className="text-[13.5px] leading-none whitespace-nowrap">
                <span className="font-bold text-[#022D41]">{count}</span>{' '}
                <span className="text-[#4A5A66] font-medium">{label}</span>
            </span>
        </button>
    );
}

export default function DashboardSummaryBar({ noPadding = false }) {
    const navigate = useNavigate();
    const approverCounts = useSelector((s) => s.timesheet?.tandecounts);
    const contractorCounts = useSelector((s) => s.timesheet?.contractortandecounts);
    const isApprover = useSelector((s) => s.accessToParts?.tAndEApprover);

    const tsRejected = contractorCounts?.Timesheet?.RejectedCount ?? 0;
    const expRejected = contractorCounts?.Expense?.RejectedCount ?? 0;
    const tsDraft = contractorCounts?.Timesheet?.DraftedCount ?? 0;
    const expDraft = contractorCounts?.Expense?.DraftedCount ?? 0;

    const tsPending = approverCounts?.PendingTimesheetCount ?? 0;
    const expPending = approverCounts?.PendingClaimsCount ?? 0;
    const tsApproved = approverCounts?.ApprovedTimesheetCount ?? 0;
    const tsApproverRejected = approverCounts?.RejectedTimesheetCount ?? 0;
    const expApproved = approverCounts?.ApprovedClaimsCount ?? 0;
    const expApproverRejected = approverCounts?.RejectedClaimsCount ?? 0;

    const contractorPills = [
        {
            id: 'ts-rejected', icon: <CancelOutlined />, count: tsRejected,
            label: 'Timesheets rejected', chipLabel: 'Timesheets rejected', sublabel: 'Action required',
            accentColor: '#EF4444', accentBg: '#FEE2E2', urgent: true,
            path: '/dashboard/timesheets?status=Rejected',
        },
        {
            id: 'exp-rejected', icon: <CancelOutlined />, count: expRejected,
            label: 'Expenses rejected', chipLabel: 'Expenses rejected', sublabel: 'Action required',
            accentColor: '#EF4444', accentBg: '#FEE2E2', urgent: true,
            path: '/dashboard/expenses?status=Rejected',
        },
        {
            id: 'ts-draft', icon: <EditNoteOutlined />, count: tsDraft,
            label: 'Draft timesheets', chipLabel: 'Timesheets pending', sublabel: 'Ready to submit',
            accentColor: '#F59E0B', accentBg: '#FEF3C7', urgent: false,
            path: '/dashboard/timesheets?status=Draft',
        },
        {
            id: 'exp-draft', icon: <ReceiptLongOutlined />, count: expDraft,
            label: 'Draft expenses', chipLabel: 'Expenses pending', sublabel: 'Ready to submit',
            accentColor: '#F59E0B', accentBg: '#FEF3C7', urgent: false,
            path: '/dashboard/expenses?status=Draft',
        }
    ];

    const approverPills = [
        {
            id: 'ts-pending', icon: <AccessTimeOutlined />, count: tsPending,
            label: 'Timesheets to review', chipLabel: 'Timesheets pending', sublabel: 'Awaiting your approval',
            accentColor: '#F59E0B', accentBg: '#FEF3C7', urgent: false,
            path: '/dashboard/timesheets?status=Pending',
        },
        {
            id: 'approved', icon: <CheckCircleOutlined />, count: tsApproved,
            label: 'Timesheets approved', chipLabel: 'Timesheets approved', sublabel: 'Approved so far',
            accentColor: '#10B981', accentBg: '#D1FAE5', urgent: false,
            path: '/dashboard/timesheets?status=Approved',
        },
        {
            id: 'rejected', icon: <CancelOutlined />, count: tsApproverRejected,
            label: 'Timesheets rejected', chipLabel: 'Timesheets rejected', sublabel: 'Sent back to contractor',
            accentColor: '#EF4444', accentBg: '#FEE2E2', urgent: false,
            path: '/dashboard/timesheets?status=Rejected',
        },
        {
            id: 'exp-pending', icon: <AttachMoneyOutlined />, count: expPending,
            label: 'Expenses to review', chipLabel: 'Expenses pending', sublabel: 'Awaiting your approval',
            accentColor: '#0094B3', accentBg: '#E6F4F8', urgent: false,
            path: '/dashboard/expenses?status=Pending',
        },
        {
            id: 'expApproved', icon: <CheckCircleOutlined />, count: expApproved,
            label: 'Expenses approved', chipLabel: 'Expenses approved', sublabel: 'Approved so far',
            accentColor: '#10B981', accentBg: '#D1FAE5', urgent: false,
            path: '/dashboard/expenses?status=Approved',
        },
        {
            id: 'expRejected', icon: <CancelOutlined />, count: expApproverRejected,
            label: 'Expenses rejected', chipLabel: 'Expenses rejected', sublabel: 'Sent back to contractor',
            accentColor: '#EF4444', accentBg: '#FEE2E2', urgent: false,
            path: '/dashboard/expenses?status=Rejected',
        },
    ];

    const pills = isApprover ? approverPills : contractorPills;
    const chipPills = pills;

    return (
        <>
            <style>{`
        @keyframes pulse-dot {
          0%, 100% { opacity: 1;   transform: scale(1);    }
          50%       { opacity: 0.4; transform: scale(0.75); }
        }
        @keyframes card-enter {
          from { opacity: 0; transform: translateY(10px); }
          to   { opacity: 1; transform: translateY(0);    }
        }
        .hide-scrollbar::-webkit-scrollbar { display: none; }
        .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>

            {/* Mobile: horizontal scroll strip, unchanged */}
            <div className={`flex md:hidden gap-2.5 mb-4 overflow-x-auto hide-scrollbar -mx-1 px-1 ${noPadding ? '' : 'px-7'}`}>
                {chipPills.map((p, i) => (
                    <SummaryChip
                        key={p.id}
                        icon={p.icon}
                        count={p.count}
                        label={p.chipLabel}
                        accentColor={p.accentColor}
                        accentBg={p.accentBg}
                        urgent={p.urgent}
                        onClick={() => navigate(p.path)}
                        style={{ animation: `card-enter 0.3s cubic-bezier(0.4,0,0.2,1) ${i * 55}ms both` }}
                    />
                ))}
            </div>

            <div
                className={[
                    'hidden gap-4 mb-5',
                    isApprover ? 'md:grid md:grid-cols-3' : 'md:flex md:flex-wrap',
                    noPadding ? '' : 'px-7',
                ].join(' ')}
            >
                {pills.map((p, i) => (
                    <SummaryPill
                        key={p.id}
                        {...p}
                        onClick={() => navigate(p.path)}
                        style={{
                            ...(isApprover ? {} : { flex: '1 1 0%', minWidth: '160px' }),
                            animation: `card-enter 0.3s cubic-bezier(0.4,0,0.2,1) ${i * 55}ms both`,
                        }}
                    />
                ))}
            </div>
        </>
    );
}